// src/components/EventManagement.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { firestore } from '../firebase';
import {
  collection,
  getDocs,
  getDoc,
  addDoc,
  deleteDoc,
  updateDoc,
  setDoc,
  doc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
} from 'firebase/firestore';
import { parseCalendarDayValue } from '../utils/eventUtils';
import {
  SCHEDULE_DAY_IDS,
  SCHEDULE_DAY_LABELS,
  ACTIVITY_OVERRIDE_FIELDS,
} from '../constants/eventActivityDates';
import {
  normalizeMarketingBaseUrl,
  DEFAULT_MARKETING_PATHS,
} from '../utils/publicSiteConfig';
import { getEffectiveLiveMode } from '../utils/eventRouting';
import { fetchSiteRegistrationSettings, saveSiteRegistrationOpen } from '../utils/siteSettings';
import { buildCurveFromFlyers, REGISTRATION_FILL_METRIC } from '../utils/registrationFillCurve';
import ScheduleEditorPanel from './ScheduleEditorPanel';
import { collectInvalidNamedControlNames } from '../utils/collectInvalidNamedControlNames';
import ModalFieldRequiredError from './ModalFieldRequiredError';
import './EventManagement.css';
import './admin/FmModal.css';
import { useModalEscapeKey } from '../utils/useModalEscapeKey';

const RULES_QR_URL = 'https://perrisfreshmeet.com/rules';

const SLIM_DEFAULT_EVENT = {
  title: '',
  editionYear: '',
  date: '',
  maxFreshlings: 112,
  registrationImportSource: 'sheet',
  furyRegistrationEventId: '',
  furyMentorsRegistrationEventId: '',
  furyJumpCompletionEventOfferingId: '',
  furyJumpCompletionDenominator: 4,
  freshlingsSpreadsheetId: '',
  freshlingsSheetName: 'Freshlings',
  mentorsSheetName: 'Mentors',
  eventManifestSheetName: 'EventManifest',
};

/** Offsets from Saturday (events.date) — must match eventUtils SCHEDULE_DAY_OFFSET */
const SCHEDULE_DAY_OFFSET_FROM_SATURDAY = {
  wednesday: -3,
  thursday: -2,
  friday: -1,
  saturday: 0,
  sunday: 1,
  monday: 2,
};

const SCHEDULE_GRID_DAY_IDS = SCHEDULE_DAY_IDS.filter((id) => id !== 'saturday');

function emptyScheduleDayDatesState() {
  return Object.fromEntries(SCHEDULE_DAY_IDS.map((id) => [id, '']));
}

function emptyActivityDatesState() {
  return Object.fromEntries(ACTIVITY_OVERRIDE_FIELDS.map(({ key }) => [key, '']));
}

function createEmptyBasicsForm() {
  return {
    ...SLIM_DEFAULT_EVENT,
    scheduleDayDates: emptyScheduleDayDatesState(),
    activityDates: emptyActivityDatesState(),
  };
}

/** YYYY-MM-DD for Wed–Sun (+ Saturday) from a Saturday anchor; empty anchor clears all. */
function computeScheduleDayDatesFromSaturday(saturdayStr) {
  const sat = parseCalendarDayValue(saturdayStr);
  if (!sat) return emptyScheduleDayDatesState();
  const next = emptyScheduleDayDatesState();
  SCHEDULE_DAY_IDS.forEach((id) => {
    const d = new Date(sat.getFullYear(), sat.getMonth(), sat.getDate());
    d.setDate(d.getDate() + SCHEDULE_DAY_OFFSET_FROM_SATURDAY[id]);
    next[id] = toDateInputValue(d);
  });
  return next;
}

function buildCalendarFieldsForPayload(newEvent) {
  const scheduleDayDates = {};
  const sat = (newEvent.date || '').trim();
  if (sat) scheduleDayDates.saturday = sat;
  SCHEDULE_GRID_DAY_IDS.forEach((id) => {
    const v = (newEvent.scheduleDayDates?.[id] || '').trim();
    if (v) scheduleDayDates[id] = v;
  });
  const activityDates = {};
  ACTIVITY_OVERRIDE_FIELDS.forEach(({ key }) => {
    const v = (newEvent.activityDates?.[key] || '').trim();
    if (v) activityDates[key] = v;
  });
  return { scheduleDayDates, activityDates };
}

const NEW_EVENT_DEFAULTS = {
  liveMode: 'adminOnly',
  isActive: false,
  isOpen: true,
  acceptingTeamNames: true,
  showLeaderboardOnHome: false,
  showBonusPuzzlesOnHome: false,
  showMorningLoadOnHome: true,
  memoryBookPublished: false,
};

function normalizeSpreadsheetInput(raw) {
  const trimmed = (raw || '').trim();
  if (!trimmed) return null;
  const match = trimmed.match(/\/d\/([a-zA-Z0-9_-]+)/);
  return match ? match[1] : trimmed;
}

function toDateInputValue(raw) {
  const d = parseCalendarDayValue(raw);
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function inferEditionYearFromDate(dateStr) {
  const m = (dateStr || '').toString().trim().match(/^(\d{4})-\d{2}-\d{2}$/);
  if (m) {
    const y = parseInt(m[1], 10);
    if (Number.isInteger(y)) return y;
  }
  return new Date().getFullYear();
}

function buildModalPayloadFromNewEvent(newEvent) {
  const rawCap = newEvent.maxFreshlings;
  const maxFreshlings =
    rawCap === '' || rawCap == null
      ? 112
      : Number.isFinite(Number(rawCap))
        ? Math.max(0, Number(rawCap))
        : 112;
  const { scheduleDayDates, activityDates } = buildCalendarFieldsForPayload(newEvent);
  const registrationImportSource =
    newEvent.registrationImportSource === 'fury_api' ? 'fury_api' : 'sheet';
  const jumpOffering = (newEvent.furyJumpCompletionEventOfferingId || '').trim();
  const denomRaw = newEvent.furyJumpCompletionDenominator;
  let furyJumpCompletionDenominator = 4;
  if (denomRaw !== '' && denomRaw != null) {
    const n = typeof denomRaw === 'number' ? denomRaw : parseInt(String(denomRaw).trim(), 10);
    if (Number.isFinite(n) && n >= 1 && n <= 99) furyJumpCompletionDenominator = n;
  }
  let editionYear = null;
  const eyRaw = newEvent.editionYear;
  if (eyRaw !== '' && eyRaw != null) {
    const n =
      typeof eyRaw === 'number' && Number.isFinite(eyRaw) ? eyRaw : parseInt(String(eyRaw).trim(), 10);
    if (!Number.isFinite(n) || n < 2000 || n > 9999) {
      throw new Error(
        'Edition year must be between 2000 and 9999, or leave blank to derive from the Saturday date.'
      );
    }
    editionYear = n;
  }
  return {
    title: newEvent.title || '',
    editionYear,
    date: newEvent.date || '',
    maxFreshlings,
    registrationImportSource,
    furyRegistrationEventId: (newEvent.furyRegistrationEventId || '').trim(),
    furyMentorsRegistrationEventId: (newEvent.furyMentorsRegistrationEventId || '').trim(),
    furyJumpCompletionEventOfferingId: registrationImportSource === 'fury_api' ? jumpOffering : '',
    furyJumpCompletionDenominator:
      registrationImportSource === 'fury_api' ? furyJumpCompletionDenominator : 4,
    freshlingsSpreadsheetId: normalizeSpreadsheetInput(newEvent.freshlingsSpreadsheetId),
    freshlingsSheetName: (newEvent.freshlingsSheetName || '').trim() || 'Freshlings',
    mentorsSheetName: (newEvent.mentorsSheetName || '').trim() || 'Mentors',
    eventManifestSheetName: (newEvent.eventManifestSheetName || '').trim() || 'EventManifest',
    scheduleDayDates,
    activityDates,
  };
}

function HelpChip({ text, label = 'Help' }) {
  return (
    <button
      type="button"
      className="event-help-chip"
      title={text}
      aria-label={`${label}: ${text}`}
      onClick={() => window.alert(text)}
    >
      ?
    </button>
  );
}

const EventManagement = () => {
  const [activeTab, setActiveTab] = useState('events');
  const [searchParams] = useSearchParams();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [eventModalInvalidNames, setEventModalInvalidNames] = useState(() => new Set());
  const [editingEventId, setEditingEventId] = useState(null);
  const [scheduleJumpEventId, setScheduleJumpEventId] = useState(null);
  const [featuredYear, setFeaturedYear] = useState(null);
  const [siteRegistrationOpen, setSiteRegistrationOpen] = useState(false);
  const [savingSiteRegistration, setSavingSiteRegistration] = useState(false);
  const [activeEventId, setActiveEventId] = useState('');
  const [volunteerEventId, setVolunteerEventId] = useState('');
  const [rootMode, setRootMode] = useState('autoLiveElseFeatured');
  const [marketingSiteBaseUrlDraft, setMarketingSiteBaseUrlDraft] = useState('');
  const [marketingPathRules, setMarketingPathRules] = useState(DEFAULT_MARKETING_PATHS.rules);
  const [savingMarketingConfig, setSavingMarketingConfig] = useState(false);

  // Modal form: title, Saturday date, week/activity calendar fields, capacity, sheets (see per-card Event settings)
  const [newEvent, setNewEvent] = useState(() => createEmptyBasicsForm());
  const [rebuildingFillEventId, setRebuildingFillEventId] = useState(null);
  const [interestEntries, setInterestEntries] = useState([]);
  const [interestLoading, setInterestLoading] = useState(false);
  const [interestError, setInterestError] = useState('');
  const [interestFilter, setInterestFilter] = useState('');
  const [editingInterestId, setEditingInterestId] = useState(null);
  const [editingInterestForm, setEditingInterestForm] = useState({ name: '', email: '', source: '' });
  const [nextYearConfig, setNextYearConfig] = useState({ headline: '', datesText: '', locationText: '', noteText: '' });
  const [savingNextYearConfig, setSavingNextYearConfig] = useState(false);

  useEffect(() => {
    if (!modalOpen) setEventModalInvalidNames(new Set());
  }, [modalOpen]);

  const closeEventBasicsModal = useCallback(() => {
    setModalOpen(false);
    setEditingEventId(null);
    setNewEvent(createEmptyBasicsForm());
  }, []);
  useModalEscapeKey(modalOpen, closeEventBasicsModal);

  // Fetch events from Firestore
  useEffect(() => {
    async function fetchEvents() {
      try {
        const qs = await getDocs(collection(firestore, 'events'));
        const eventsData = qs.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setEvents(eventsData);
        setLoading(false);
      } catch (err) {
        setError('Failed to fetch events: ' + err.message);
        setLoading(false);
      }
    }
    fetchEvents();
  }, [searchParams]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(firestore, '_config', 'publicSite'));
        if (cancelled) return;
        const yRaw = snap.exists() ? snap.data()?.marketingFeaturedYear : null;
        const y = Number.parseInt(String(yRaw ?? '').trim(), 10);
        setFeaturedYear(Number.isInteger(y) && y >= 2000 && y <= 9999 ? y : null);
        const modeRaw = snap.exists() ? (snap.data()?.rootMode || '') : '';
        const mode = (modeRaw || '').toString().trim();
        setRootMode(mode === 'featuredOnly' ? 'featuredOnly' : 'autoLiveElseFeatured');
        const baseRaw = snap.exists() ? snap.data()?.marketingSiteBaseUrl : '';
        setMarketingSiteBaseUrlDraft(normalizeMarketingBaseUrl(baseRaw));
        const mp = snap.exists() && snap.data()?.marketingPaths && typeof snap.data().marketingPaths === 'object'
          ? snap.data().marketingPaths
          : {};
        setMarketingPathRules(
          typeof mp.rules === 'string' && mp.rules.trim() ? mp.rules.trim() : DEFAULT_MARKETING_PATHS.rules
        );
      } catch {
        if (!cancelled) setFeaturedYear(null);
        if (!cancelled) setRootMode('autoLiveElseFeatured');
        if (!cancelled) {
          setMarketingSiteBaseUrlDraft('');
          setMarketingPathRules(DEFAULT_MARKETING_PATHS.rules);
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(firestore, '_config', 'system'));
        if (cancelled) return;
        const id = snap.exists() ? (snap.data()?.activeEventId || '') : '';
        const volunteerId = snap.exists() ? (snap.data()?.volunteerEventId || '') : '';
        setActiveEventId((id || '').toString());
        setVolunteerEventId((volunteerId || '').toString());
      } catch {
        if (!cancelled) setActiveEventId('');
        if (!cancelled) setVolunteerEventId('');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const getEventYear = (ev) => {
    const raw = ev?.editionYear;
    const y = Number.parseInt(String(raw ?? '').trim(), 10);
    if (Number.isInteger(y) && y >= 2000 && y <= 9999) return y;
    const dateStr = (ev?.date || '').toString().trim();
    const m = dateStr.match(/^(\d{4})-\d{2}-\d{2}$/);
    if (m) {
      const y2 = Number.parseInt(m[1], 10);
      if (Number.isInteger(y2)) return y2;
    }
    return null;
  };

  const defaultEventStatus = useMemo(() => {
    const id = (activeEventId || '').trim();
    if (!id) return { text: '—', title: undefined };
    if (loading) return { text: '…', title: `Document ID: ${id}` };
    const ev = events.find((e) => e.id === id);
    if (!ev) {
      return {
        text: 'Unknown event',
        title: `No event in this list matches document ID: ${id}`,
      };
    }
    const y = getEventYear(ev);
    const name = (ev.title || '').trim() || 'Untitled';
    const text = y != null ? `${name} (${y})` : name;
    return { text, title: `Document ID: ${id}` };
  }, [activeEventId, events, loading]);

  const volunteerEventStatus = useMemo(() => {
    const id = (volunteerEventId || '').trim();
    if (!id) return { text: '—', title: 'Falls back to public default event selection.' };
    if (loading) return { text: '…', title: `Document ID: ${id}` };
    const ev = events.find((e) => e.id === id);
    if (!ev) {
      return {
        text: 'Unknown event',
        title: `No event in this list matches document ID: ${id}`,
      };
    }
    const y = getEventYear(ev);
    const name = (ev.title || '').trim() || 'Untitled';
    const text = y != null ? `${name} (${y})` : name;
    return { text, title: `Document ID: ${id}` };
  }, [volunteerEventId, events, loading]);

  const baseSiteUrl = (process.env.REACT_APP_SITE_URL || window.location.origin).replace(/\/$/, '');

  const patchEventFields = async (eventId, patch) => {
    if (!eventId || !patch || typeof patch !== 'object') return;
    try {
      setError('');
      await updateDoc(doc(firestore, 'events', eventId), patch);
      setEvents((prev) => prev.map((row) => (row.id === eventId ? { ...row, ...patch } : row)));
    } catch (err) {
      setError(err?.message || 'Failed to save.');
    }
  };

  const openAddEventModal = () => {
    setEditingEventId(null);
    setNewEvent(createEmptyBasicsForm());
    setModalOpen(true);
  };

  const setRootModeConfig = async (mode) => {
    const next = mode === 'featuredOnly' ? 'featuredOnly' : 'autoLiveElseFeatured';
    try {
      setError('');
      await setDoc(doc(firestore, '_config', 'publicSite'), { rootMode: next, updatedAt: serverTimestamp() }, { merge: true });
      setRootMode(next);
    } catch (err) {
      setError(err?.message || 'Failed to save root mode.');
    }
  };

  const saveMarketingSiteConfig = async () => {
    try {
      setSavingMarketingConfig(true);
      setError('');
      const base = normalizeMarketingBaseUrl(marketingSiteBaseUrlDraft);
      const snap = await getDoc(doc(firestore, '_config', 'publicSite'));
      const mp =
        snap.exists() && snap.data()?.marketingPaths && typeof snap.data().marketingPaths === 'object'
          ? snap.data().marketingPaths
          : {};
      const map =
        typeof mp.map === 'string' && mp.map.trim() ? mp.map.trim() : DEFAULT_MARKETING_PATHS.map;
      const location =
        typeof mp.location === 'string' && mp.location.trim()
          ? mp.location.trim()
          : DEFAULT_MARKETING_PATHS.location;
      await setDoc(
        doc(firestore, '_config', 'publicSite'),
        {
          marketingSiteBaseUrl: base || null,
          marketingPaths: {
            rules: (marketingPathRules || '').trim() || DEFAULT_MARKETING_PATHS.rules,
            map,
            location,
          },
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      setMarketingSiteBaseUrlDraft(base);
    } catch (err) {
      setError(err?.message || 'Failed to save marketing site settings.');
    } finally {
      setSavingMarketingConfig(false);
    }
  };

  const setFeaturedToEventYear = async (ev) => {
    const y = getEventYear(ev);
    if (!y) {
      window.alert('This event needs an Edition year (or a YYYY-MM-DD date) to set it as featured.');
      return;
    }
    if (!window.confirm(`Set root (/) featured year to ${y}?\n\nThis controls where / redirects when no edition is in public live mode.`)) {
      return;
    }
    try {
      setError('');
      await setDoc(doc(firestore, '_config', 'publicSite'), { marketingFeaturedYear: y, updatedAt: serverTimestamp() }, { merge: true });
      setFeaturedYear(y);
    } catch (err) {
      setError(err?.message || 'Failed to set featured year.');
    }
  };

  const togglePuzzleScoring = async (ev) => {
    if (!ev?.id) return;
    const next = ev.isOpen === false;
    const label = next ? 'Open bonus puzzle scoring' : 'Close bonus puzzle scoring';
    if (!window.confirm(`${label} for "${ev.title || ev.id}"?\n\nWhen closed, teams can still open puzzle pages, but submissions will be blocked.`)) return;
    try {
      setError('');
      await updateDoc(doc(firestore, 'events', ev.id), { isOpen: next });
      setEvents((prev) => prev.map((row) => (row.id === ev.id ? { ...row, isOpen: next } : row)));
    } catch (err) {
      setError(err?.message || 'Failed to update puzzle scoring.');
    }
  };

  const setActiveDefaultsEvent = async (ev) => {
    if (!ev?.id) return;
    if (!window.confirm(`Make default event:\n\n${ev.title || ev.id}\n\nThis changes which event many admin pages use when no event is specified.`)) {
      return;
    }
    try {
      setError('');
      await setDoc(doc(firestore, '_config', 'system'), { activeEventId: ev.id, updatedAt: serverTimestamp() }, { merge: true });
      // Keep legacy isActive in sync during migration.
      const updates = events.map((row) =>
        updateDoc(doc(firestore, 'events', row.id), { isActive: row.id === ev.id })
      );
      await Promise.all(updates);
      setActiveEventId(ev.id);
      setEvents((prev) => prev.map((row) => (row.id === ev.id ? { ...row, isActive: true } : { ...row, isActive: false })));
    } catch (err) {
      setError(err?.message || 'Failed to set default event.');
    }
  };

  const setVolunteerDefaultEvent = async (ev) => {
    if (!ev?.id) return;
    if (
      !window.confirm(
        `Set volunteer default event:\n\n${ev.title || ev.id}\n\nThis controls which event /volunteer uses when no ?event= is provided.`
      )
    ) {
      return;
    }
    try {
      setError('');
      await setDoc(
        doc(firestore, '_config', 'system'),
        { volunteerEventId: ev.id, updatedAt: serverTimestamp() },
        { merge: true }
      );
      setVolunteerEventId(ev.id);
    } catch (err) {
      setError(err?.message || 'Failed to set volunteer default event.');
    }
  };

  const loadInterestEntries = async () => {
    setInterestLoading(true);
    setInterestError('');
    try {
      const q = query(collection(firestore, 'nextYearInterest'), orderBy('createdAt', 'desc'), limit(500));
      const snap = await getDocs(q);
      setInterestEntries(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      setInterestError(err?.message || 'Failed to load interest list.');
    } finally {
      setInterestLoading(false);
    }
  };

  const loadNextYearConfig = async () => {
    try {
      const snap = await getDoc(doc(firestore, '_config', 'nextYearInterestConfig'));
      const data = snap.exists() ? snap.data() : {};
      setNextYearConfig({
        headline: (data.headline || '').toString(),
        datesText: (data.datesText || '').toString(),
        locationText: (data.locationText || '').toString(),
        noteText: (data.noteText || '').toString(),
      });
    } catch (err) {
      setInterestError((prev) => prev || (err?.message || 'Failed to load next year config.'));
    }
  };

  useEffect(() => {
    if (activeTab !== 'marketing') return;
    loadInterestEntries().catch(() => {});
    loadNextYearConfig().catch(() => {});
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== 'events') return;
    let cancelled = false;
    fetchSiteRegistrationSettings()
      .then((reg) => {
        if (!cancelled) setSiteRegistrationOpen(!!reg.registrationOpen);
      })
      .catch(() => {
        if (!cancelled) setSiteRegistrationOpen(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeTab]);

  const handleToggleSiteRegistration = async () => {
    const next = !siteRegistrationOpen;
    setSavingSiteRegistration(true);
    setError('');
    try {
      await saveSiteRegistrationOpen(next);
      setSiteRegistrationOpen(next);
    } catch (err) {
      setError(err?.message || 'Failed to save registration setting.');
    } finally {
      setSavingSiteRegistration(false);
    }
  };

  const filteredInterestEntries = useMemo(() => {
    const term = (interestFilter || '').trim().toLowerCase();
    if (!term) return interestEntries;
    return interestEntries.filter((row) => {
      const name = (row.name || '').toString().toLowerCase();
      const email = (row.email || '').toString().toLowerCase();
      const source = (row.source || '').toString().toLowerCase();
      const year = row.fromEditionYear != null ? String(row.fromEditionYear) : '';
      return name.includes(term) || email.includes(term) || source.includes(term) || year.includes(term);
    });
  }, [interestEntries, interestFilter]);

  const formatCreatedAt = (ts) => {
    try {
      if (!ts || typeof ts.toDate !== 'function') return '—';
      return ts.toDate().toLocaleString();
    } catch {
      return '—';
    }
  };

  const handleStartEditInterest = (row) => {
    setEditingInterestId(row.id);
    setEditingInterestForm({
      name: (row.name || '').toString(),
      email: (row.email || '').toString(),
      source: (row.source || '').toString(),
    });
  };

  const handleCancelEditInterest = () => {
    setEditingInterestId(null);
    setEditingInterestForm({ name: '', email: '', source: '' });
  };

  const handleSaveEditInterest = async (id) => {
    try {
      await updateDoc(doc(firestore, 'nextYearInterest', id), {
        name: editingInterestForm.name.trim(),
        email: editingInterestForm.email.trim().toLowerCase(),
        source: editingInterestForm.source.trim(),
      });
      setInterestEntries((prev) => prev.map((r) => (r.id === id ? { ...r, ...editingInterestForm } : r)));
      handleCancelEditInterest();
    } catch (err) {
      window.alert(err?.message || 'Failed to save interest entry.');
    }
  };

  const handleDeleteInterest = async (id) => {
    if (!window.confirm('Delete this interest list entry?')) return;
    try {
      await deleteDoc(doc(firestore, 'nextYearInterest', id));
      setInterestEntries((prev) => prev.filter((r) => r.id !== id));
      if (editingInterestId === id) handleCancelEditInterest();
    } catch (err) {
      window.alert(err?.message || 'Failed to delete entry.');
    }
  };

  const handleCopyEmails = async () => {
    const uniqueEmails = Array.from(
      new Set(
        filteredInterestEntries
          .map((r) => (r.email || '').toString().trim().toLowerCase())
          .filter(Boolean)
      )
    );
    try {
      await navigator.clipboard.writeText(uniqueEmails.join('\n'));
      window.alert(`Copied ${uniqueEmails.length} email(s).`);
    } catch {
      window.alert('Unable to copy emails in this browser.');
    }
  };

  const handleDownloadInterestCsv = () => {
    const escapeCsv = (value) => `"${String(value ?? '').replaceAll('"', '""')}"`;
    const header = ['name', 'email', 'fromEditionYear', 'source', 'createdAt'];
    const rows = filteredInterestEntries.map((r) => ([
      r.name || '',
      r.email || '',
      r.fromEditionYear ?? '',
      r.source || '',
      formatCreatedAt(r.createdAt),
    ]));
    const csv = [header.join(','), ...rows.map((row) => row.map(escapeCsv).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const stamp = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `freshmeet-interest-list-${stamp}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleSaveNextYearConfig = async () => {
    setSavingNextYearConfig(true);
    try {
      await setDoc(doc(firestore, '_config', 'nextYearInterestConfig'), {
        headline: nextYearConfig.headline || '',
        datesText: nextYearConfig.datesText || '',
        locationText: nextYearConfig.locationText || '',
        noteText: nextYearConfig.noteText || '',
        updatedAt: serverTimestamp(),
      }, { merge: true });
      window.alert('Next year interest config saved.');
    } catch (err) {
      window.alert(err?.message || 'Failed to save next year config.');
    } finally {
      setSavingNextYearConfig(false);
    }
  };

  // Handle form submission: modal updates title, edition year, dates, capacity, sheets; other fields live on the card
  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    if (!form.checkValidity()) {
      setEventModalInvalidNames(collectInvalidNamedControlNames(form));
      const inv = form.querySelector(':invalid');
      if (inv && typeof inv.focus === 'function') inv.focus();
      return;
    }
    setEventModalInvalidNames(new Set());
    try {
      const modalPayload = buildModalPayloadFromNewEvent(newEvent);

      if (editingEventId) {
        const eventRef = doc(firestore, 'events', editingEventId);
        await updateDoc(eventRef, modalPayload);
        setEvents((prev) =>
          prev.map((ev) => (ev.id === editingEventId ? { ...ev, ...modalPayload } : ev))
        );
        setEditingEventId(null);
      } else {
        const editionYearResolved =
          modalPayload.editionYear != null
            ? modalPayload.editionYear
            : inferEditionYearFromDate(modalPayload.date);
        const eventData = {
          ...NEW_EVENT_DEFAULTS,
          ...modalPayload,
          editionYear: editionYearResolved,
        };
        const docRef = await addDoc(collection(firestore, 'events'), eventData);
        setEvents((prev) => [...prev, { id: docRef.id, ...eventData }]);
      }
      setNewEvent(createEmptyBasicsForm());
      setModalOpen(false);
    } catch (err) {
      setError('Error saving event: ' + err.message);
    }
  };

  const handleDelete = async (eventId) => {
    if (window.confirm("Are you sure you want to delete this event?")) {
      try {
        await deleteDoc(doc(firestore, 'events', eventId));
        setEvents(events.filter(ev => ev.id !== eventId));
      } catch (err) {
        setError('Error deleting event: ' + err.message);
      }
    }
  };

  const handleEdit = (event) => {
    setEditingEventId(event.id);
    const dateInput = toDateInputValue(event.date);
    const sd = event.scheduleDayDates && typeof event.scheduleDayDates === 'object' ? event.scheduleDayDates : {};
    const computed = computeScheduleDayDatesFromSaturday(dateInput);
    const scheduleDayDates = emptyScheduleDayDatesState();
    SCHEDULE_DAY_IDS.forEach((id) => {
      scheduleDayDates[id] = toDateInputValue(sd[id]) || computed[id] || '';
    });
    const activityDates = emptyActivityDatesState();
    ACTIVITY_OVERRIDE_FIELDS.forEach(({ key }) => {
      activityDates[key] = toDateInputValue(event.activityDates?.[key]) || '';
    });
    setNewEvent({
      title: event.title || '',
      editionYear:
        event.editionYear != null && event.editionYear !== ''
          ? String(event.editionYear)
          : '',
      date: dateInput,
      maxFreshlings: event.maxFreshlings != null ? event.maxFreshlings : 112,
      registrationImportSource: event.registrationImportSource === 'fury_api' ? 'fury_api' : 'sheet',
      furyRegistrationEventId: event.furyRegistrationEventId || '',
      furyMentorsRegistrationEventId: event.furyMentorsRegistrationEventId || '',
      furyJumpCompletionEventOfferingId: event.furyJumpCompletionEventOfferingId || '',
      furyJumpCompletionDenominator: (() => {
        const d = event.furyJumpCompletionDenominator;
        if (d == null || d === '') return 4;
        const n = Number(d);
        return Number.isFinite(n) && n >= 1 && n <= 99 ? n : 4;
      })(),
      freshlingsSpreadsheetId: event.freshlingsSpreadsheetId || '',
      freshlingsSheetName: event.freshlingsSheetName || 'Freshlings',
      mentorsSheetName: event.mentorsSheetName || 'Mentors',
      eventManifestSheetName: event.eventManifestSheetName || 'EventManifest',
      scheduleDayDates,
      activityDates,
    });
    setModalOpen(true);
  };

  const handleScheduleSaved = (eventId, { scheduleData, scheduleHtml }) => {
    setEvents((prev) =>
      prev.map((ev) => (ev.id === eventId ? { ...ev, scheduleData, scheduleHtml } : ev))
    );
  };

  const handleRebuildFillFromFlyers = async (ev) => {
    if (!ev?.id) return;
    setRebuildingFillEventId(ev.id);
    setError('');
    try {
      const q = query(
        collection(firestore, 'flyers'),
        where('eventId', '==', ev.id),
        where('type', '==', 'freshling')
      );
      const snap = await getDocs(q);
      const flyers = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const built = buildCurveFromFlyers(flyers, ev);
      const payload = {
        metric: REGISTRATION_FILL_METRIC,
        points: built.points,
        finalC: built.finalC,
        source: 'flyers',
        updatedAt: serverTimestamp(),
      };
      if (built.skippedNoCreatedAt) payload.skippedNoCreatedAt = built.skippedNoCreatedAt;
      if (built.skippedAfterEvent) payload.skippedAfterEvent = built.skippedAfterEvent;
      await updateDoc(doc(firestore, 'events', ev.id), { registrationFillCurve: payload });
      setEvents((prev) =>
        prev.map((row) =>
          row.id === ev.id
            ? {
                ...row,
                registrationFillCurve: {
                  metric: payload.metric,
                  points: payload.points,
                  finalC: payload.finalC,
                  source: payload.source,
                  ...(payload.skippedNoCreatedAt ? { skippedNoCreatedAt: payload.skippedNoCreatedAt } : {}),
                },
              }
            : row
        )
      );
      window.alert(
        `Fill curve saved: ${built.finalC} gross signups from flyer createdAt. Skipped ${built.skippedNoCreatedAt} with no createdAt.`
      );
    } catch (err) {
      console.error(err);
      setError(err.message || 'Rebuild fill curve failed.');
    } finally {
      setRebuildingFillEventId(null);
    }
  };

  const handlePrintRulesQR = () => {
    const url = RULES_QR_URL;
    const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(url)}`;
    const html = `<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>Rules QR</title>
<style>
  body { margin: 0; font-family: system-ui, sans-serif; display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 100vh; padding: 1rem; }
  .no-print { margin-bottom: 1rem; }
  .no-print button { padding: 8px 16px; cursor: pointer; background: #ea580c; color: #fff; border: none; border-radius: 6px; }
  h1 { font-size: 1.5rem; color: #9a3412; margin: 0 0 0.5rem 0; }
  p { margin: 0 0 1rem 0; color: #555; }
  img { display: block; }
  @media print { .no-print { display: none !important; } }
</style></head>
<body>
  <div class="no-print"><button onclick="window.print()">Print</button> <button onclick="window.close()">Close</button></div>
  <h1>Fresh Meet Rules</h1>
  <p>Scan to open the rules page</p>
  <img src="${qrSrc}" alt="QR Code" width="300" height="300" />
</body></html>`;
    const win = window.open('', '_blank');
    if (win?.document) {
      win.document.write(html);
      win.document.close();
    }
  };

  return (
    <div className="event-management">
      <header className="event-management-header">
        <h2>Event Management</h2>
        <div className="event-management-header-actions">
          {activeTab === 'events' && (
            <button type="button" onClick={openAddEventModal} className="add-event-button">
              Add Event
            </button>
          )}
        </div>
      </header>
      {activeTab === 'events' && (
        <section className="event-management-statusbar" aria-label="Site status">
          <div className="event-management-statusbar-row">
            <div className="event-management-statusbar-item">
              <span className="event-management-statusbar-label">
                Root mode
                <HelpChip
                  label="Root mode help"
                  text={
                    'The public home path "/" redirects to the marketing site (configure base URL and rules path on the Marketing tab). Root mode still controls getFeaturedYear(): auto-live else featured picks the live public year when set, otherwise the featured year — used for /schedule, /dashboard, and other public defaults.'
                  }
                />
              </span>
              <select value={rootMode} onChange={(e) => setRootModeConfig(e.target.value)} className="event-management-statusbar-select">
                <option value="autoLiveElseFeatured">Auto-live else featured</option>
                <option value="featuredOnly">Featured only</option>
              </select>
            </div>
            <div className="event-management-statusbar-item">
              <span className="event-management-statusbar-label">
                Featured year
                <HelpChip
                  label="Featured year help"
                  text={'Featured year is the fallback year for "/" and old links when no edition is in public live mode. Setting Featured does not change the default event.'}
                />
              </span>
              <span className="event-management-statusbar-value">{featuredYear || '—'}</span>
            </div>
            <div className="event-management-statusbar-item">
              <span className="event-management-statusbar-label">
                Default event
                <HelpChip
                  label="Default event help"
                  text={'The default event is mainly for admin pages and some fallbacks. It does not force "/" if Root mode points to Live or Featured year.'}
                />
              </span>
              <span className="event-management-statusbar-value" title={defaultEventStatus.title}>
                {defaultEventStatus.text}
              </span>
            </div>
            <div className="event-management-statusbar-item">
              <span className="event-management-statusbar-label">
                Volunteer default
                <HelpChip
                  label="Volunteer default help"
                  text={'Used by /volunteer when no ?event= is present. If unset, /volunteer falls back to public default event selection.'}
                />
              </span>
              <span className="event-management-statusbar-value" title={volunteerEventStatus.title}>
                {volunteerEventStatus.text}
              </span>
            </div>
            <div className="event-management-statusbar-item">
              <span className="event-management-statusbar-label">
                Registration
                <HelpChip
                  label="Registration help"
                  text="Writes site/settings.registrationOpen (used by schedule CTAs and the marketing site flow)."
                />
              </span>
              <button
                type="button"
                className="btn btn-outline"
                onClick={handleToggleSiteRegistration}
                disabled={savingSiteRegistration}
              >
                {savingSiteRegistration ? 'Saving…' : siteRegistrationOpen ? 'Reg open' : 'Reg closed'}
              </button>
            </div>
          </div>
        </section>
      )}
      <div className="event-management-tabs" role="tablist" aria-label="Events sections">
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'events'}
          className={`event-management-tab ${activeTab === 'events' ? 'event-management-tab--active' : ''}`}
          onClick={() => setActiveTab('events')}
        >
          Events
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'marketing'}
          className={`event-management-tab ${activeTab === 'marketing' ? 'event-management-tab--active' : ''}`}
          onClick={() => setActiveTab('marketing')}
        >
          Marketing
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'schedule'}
          className={`event-management-tab ${activeTab === 'schedule' ? 'event-management-tab--active' : ''}`}
          onClick={() => setActiveTab('schedule')}
        >
          Schedule
        </button>
      </div>
      {error && <p className="error">{error}</p>}

      {activeTab === 'events' && (loading ? (
        <div className="event-management-loading">Loading events…</div>
      ) : events.length === 0 ? (
        <div className="event-management-empty">
          <p>No events yet. Add an event to get started.</p>
          <button type="button" onClick={openAddEventModal} className="add-event-button">Add Event</button>
        </div>
      ) : (
        <div className="event-cards">
          {events
            .slice()
            .sort((a, b) => {
              const ya = getEventYear(a) ?? -1;
              const yb = getEventYear(b) ?? -1;
              if (ya !== yb) return yb - ya;
              const ta = (a.title || '').toString();
              const tb = (b.title || '').toString();
              return ta.localeCompare(tb);
            })
            .map(ev => {
              const y = getEventYear(ev);
              const liveModeEffective = getEffectiveLiveMode(ev);
              const liveBadgeClass = liveModeEffective === 'public' ? 'live' : 'post';
              const liveModeLabel = liveModeEffective === 'public' ? 'Live: public' : 'Live: admin only';
              const isFeatured = y != null && featuredYear != null && y === featuredYear;
              const memoryBook = ev.memoryBookPublished === true;

              const publicIndexUrl = y != null ? `${baseSiteUrl}/${y}` : null;
              const publicLiveUrl = y != null ? `${baseSiteUrl}/${y}/live` : null;
              const publicArchiveUrl = y != null ? `${baseSiteUrl}/${y}/archive` : null;
              const adminLivePreviewUrl = y != null ? `/admin/live/${y}` : null;

              return (
            <article key={ev.id} className="event-card">
              <div className="event-card-header">
                <div className="event-card-title-row">
                  <h3 className="event-card-title">{ev.title}</h3>
                  {y != null && <span className="event-card-year">({y})</span>}
                  <span className="event-card-date">{ev.date || '—'}</span>
                </div>
                <div className="event-card-badges">
                  {ev.isActive && <span className="badge badge-active">Active</span>}
                  {activeEventId && ev.id === activeEventId && (
                    <span className="badge badge-active-defaults" title="Default for pages without an explicit event">
                      Default
                    </span>
                  )}
                  {isFeatured && <span className="badge badge-featured" title="Root / redirects to this year">Featured</span>}
                  <span className={`badge badge-phase badge-phase-${liveBadgeClass}`}>{liveModeLabel}</span>
                  <span className={`badge badge-${ev.isOpen ? 'open' : 'closed'}`}>{ev.isOpen ? 'Bonus scoring open' : 'Bonus scoring closed'}</span>
                  {ev.acceptingTeamNames !== false && <span className="badge badge-teams">Team names</span>}
                  {memoryBook && <span className="badge badge-memorybook">Memory book published</span>}
                </div>
                <div className="event-card-subrow">
                  <div className="event-card-subrow-left">
                    <span className="event-card-meta">
                      <span className="event-card-meta-label">Event ID:</span> <span className="event-card-meta-mono">{ev.id}</span>
                    </span>
                  </div>
                  <div className="event-card-subrow-right">
                    <div className="event-card-dashboard-toggles" role="group" aria-label="Public team dashboard links for this event">
                      <span className="event-card-meta-label">Dashboard:</span>
                      <div className="event-card-dashboard-toggle-list">
                        <label className="event-card-dashboard-toggle">
                          <input
                            type="checkbox"
                            checked={!!ev.showLeaderboardOnHome}
                            onChange={(e) => patchEventFields(ev.id, { showLeaderboardOnHome: e.target.checked })}
                          />
                          <span>Leaderboard</span>
                        </label>
                        <span className="event-card-dashboard-sep" aria-hidden="true">·</span>
                        <label className="event-card-dashboard-toggle">
                          <input
                            type="checkbox"
                            checked={!!ev.showBonusPuzzlesOnHome}
                            onChange={(e) => patchEventFields(ev.id, { showBonusPuzzlesOnHome: e.target.checked })}
                          />
                          <span>Bonus</span>
                        </label>
                        <span className="event-card-dashboard-sep" aria-hidden="true">·</span>
                        <label className="event-card-dashboard-toggle">
                          <input
                            type="checkbox"
                            checked={ev.showMorningLoadOnHome !== false}
                            onChange={(e) => patchEventFields(ev.id, { showMorningLoadOnHome: e.target.checked })}
                          />
                          <span>Load order</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="event-card-actions">
                <div className="event-card-actions-column">
                  <div className="event-card-actions-label">Basics</div>
                  <div className="event-card-actions-group">
                    <button type="button" onClick={() => handleEdit(ev)} className="btn btn-fm-primary">Edit basics</button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => {
                        setScheduleJumpEventId(ev.id);
                        setActiveTab('schedule');
                      }}
                    >
                      Schedule
                    </button>
                    <button type="button" onClick={() => setActiveDefaultsEvent(ev)} className="btn btn-outline" disabled={!!activeEventId && ev.id === activeEventId}>
                      {activeEventId && ev.id === activeEventId ? 'Default' : 'Make Default'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setVolunteerDefaultEvent(ev)}
                      className="btn btn-outline"
                      disabled={!!volunteerEventId && ev.id === volunteerEventId}
                      title="Set as /volunteer default event when no ?event= is provided"
                    >
                      {volunteerEventId && ev.id === volunteerEventId ? 'Volunteer default' : 'Set volunteer default'}
                    </button>
                    <button type="button" onClick={() => setFeaturedToEventYear(ev)} className="btn btn-outline" disabled={isFeatured || y == null}>
                      {isFeatured ? 'Featured' : 'Set featured'}
                    </button>
                  </div>
                </div>
                <div className="event-card-actions-column">
                  <div className="event-card-actions-label">
                    Live mode
                    <HelpChip
                      label="Live mode help"
                      text={'Public: team dashboard and /year/live are open to everyone. Admin only: non-admins are redirected to the archive; admins can still open the dashboard and /admin/live/YEAR preview.'}
                    />
                  </div>
                  <div className="event-card-actions-group">
                    <select
                      className="event-card-phase-select"
                      value={liveModeEffective}
                      onChange={(e) => {
                        const next = e.target.value;
                        patchEventFields(ev.id, { liveMode: next });
                      }}
                    >
                      <option value="public">Fully active (public)</option>
                      <option value="adminOnly">Admin only</option>
                    </select>
                  </div>
                </div>
                <div className="event-card-actions-column">
                  <div className="event-card-actions-label">Team dashboard &amp; book</div>
                  <div className="event-card-actions-group event-card-actions-group--stacked">
                    <button type="button" className="btn btn-outline" onClick={() => togglePuzzleScoring(ev)}>
                      {ev.isOpen === false ? 'Open bonus scoring' : 'Close bonus scoring'}
                    </button>
                    <label className="event-card-inline-checkbox">
                      <input
                        type="checkbox"
                        checked={ev.acceptingTeamNames !== false}
                        onChange={(e) => patchEventFields(ev.id, { acceptingTeamNames: e.target.checked })}
                      />
                      <span>Accepting team name changes</span>
                    </label>
                    <label className="event-card-inline-checkbox">
                      <input
                        type="checkbox"
                        checked={!!ev.memoryBookPublished}
                        onChange={(e) => patchEventFields(ev.id, { memoryBookPublished: e.target.checked })}
                      />
                      <span>Publish memory book (archive)</span>
                    </label>
                  </div>
                </div>
                <div className="event-card-actions-column">
                  <div className="event-card-actions-label">Links</div>
                  <div className="event-card-actions-group">
                    {publicIndexUrl ? (
                      <a className="btn btn-outline" href={publicIndexUrl} target="_blank" rel="noopener noreferrer">
                        Year hub
                      </a>
                    ) : (
                      <span className="btn btn-outline" aria-disabled="true" title="Set Edition year to enable public links">Year hub</span>
                    )}
                    {publicLiveUrl ? (
                      <a className="btn btn-outline" href={publicLiveUrl} target="_blank" rel="noopener noreferrer">
                        Team dashboard
                      </a>
                    ) : null}
                    {publicArchiveUrl ? (
                      <a className="btn btn-outline" href={publicArchiveUrl} target="_blank" rel="noopener noreferrer">
                        Archive / memory book
                      </a>
                    ) : null}
                    {adminLivePreviewUrl ? (
                      <a className="btn btn-outline" href={adminLivePreviewUrl} title="Admin-only live preview for this year">
                        Admin live preview
                      </a>
                    ) : null}
                  </div>
                </div>
                <div className="event-card-actions-column">
                  <div className="event-card-actions-label">Data tools</div>
                  <div className="event-card-actions-group">
                    <button
                      type="button"
                      onClick={() => handleRebuildFillFromFlyers(ev)}
                      className="btn btn-outline"
                      disabled={rebuildingFillEventId === ev.id}
                      title="Saves registration fill curve from this event’s freshling flyer createdAt (gross signups). Runs for this row only — open the 2026 event and click here to add 2026 to the dashboard chart when 2027 is active."
                    >
                      {rebuildingFillEventId === ev.id ? 'Rebuilding…' : 'Rebuild fill curve'}
                    </button>
                    <button type="button" onClick={() => handlePrintRulesQR()} className="btn btn-outline">
                      Rules QR
                    </button>
                  </div>
                </div>
                <button type="button" onClick={() => handleDelete(ev.id)} className="btn btn-danger">Delete</button>
              </div>
            </article>
              );
            })}
        </div>
      ))}

      {activeTab === 'marketing' && (
        <section className="marketing-panel">
          <div className="marketing-card event-management-marketing-config">
            <h3 className="event-management-marketing-config-title">Marketing site (static)</h3>
            <p className="event-management-marketing-config-help">
              Used for &quot;/&quot; redirect, <code>/rules</code> redirect, and when the team dashboard is closed to the public.
              Map and location paths on the marketing site use defaults (<code>{DEFAULT_MARKETING_PATHS.map}</code>, location anchor) and are not edited here.
            </p>
            <div className="event-management-marketing-grid">
              <label className="event-management-marketing-field">
                <span>Marketing site base URL (no trailing slash)</span>
                <input
                  type="url"
                  value={marketingSiteBaseUrlDraft}
                  onChange={(e) => setMarketingSiteBaseUrlDraft(e.target.value)}
                  placeholder="https://perrisfreshmeet.com"
                  className="event-management-marketing-input"
                />
              </label>
              <label className="event-management-marketing-field">
                <span>Rules path</span>
                <input
                  type="text"
                  value={marketingPathRules}
                  onChange={(e) => setMarketingPathRules(e.target.value)}
                  className="event-management-marketing-input"
                />
              </label>
            </div>
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => saveMarketingSiteConfig()}
              disabled={savingMarketingConfig}
            >
              {savingMarketingConfig ? 'Saving…' : 'Save marketing URLs'}
            </button>
          </div>

          <div className="marketing-card">
            <h3>Next year interest modal content</h3>
            <div className="marketing-config-grid">
              <label>
                Headline
                <input
                  type="text"
                  value={nextYearConfig.headline}
                  onChange={(e) => setNextYearConfig((p) => ({ ...p, headline: e.target.value }))}
                  placeholder="Ex: Fresh Meet {year}"
                />
              </label>
              <label>
                Dates
                <input
                  type="text"
                  value={nextYearConfig.datesText}
                  onChange={(e) => setNextYearConfig((p) => ({ ...p, datesText: e.target.value }))}
                  placeholder="Ex: March 17-21, {year}"
                />
              </label>
              <label>
                Location
                <input
                  type="text"
                  value={nextYearConfig.locationText}
                  onChange={(e) => setNextYearConfig((p) => ({ ...p, locationText: e.target.value }))}
                  placeholder="Ex: Skydive Perris"
                />
              </label>
              <label>
                Note
                <textarea
                  rows={2}
                  value={nextYearConfig.noteText}
                  onChange={(e) => setNextYearConfig((p) => ({ ...p, noteText: e.target.value }))}
                  placeholder="Optional note"
                />
              </label>
            </div>
            <button type="button" className="btn btn-fm-primary" onClick={handleSaveNextYearConfig} disabled={savingNextYearConfig}>
              {savingNextYearConfig ? 'Saving…' : 'Save modal content'}
            </button>
          </div>

          <div className="marketing-card">
            <div className="marketing-interest-header">
              <h3>Interest list signups</h3>
              <div className="marketing-interest-actions">
                <input
                  type="text"
                  value={interestFilter}
                  onChange={(e) => setInterestFilter(e.target.value)}
                  placeholder="Filter name/email/source/year"
                />
                <button type="button" className="btn btn-outline" onClick={loadInterestEntries} disabled={interestLoading}>
                  {interestLoading ? 'Loading…' : 'Refresh'}
                </button>
                <button type="button" className="btn btn-outline" onClick={handleCopyEmails} disabled={!filteredInterestEntries.length}>
                  Copy emails
                </button>
                <button type="button" className="btn btn-outline" onClick={handleDownloadInterestCsv} disabled={!filteredInterestEntries.length}>
                  Download CSV
                </button>
              </div>
            </div>
            {interestError && <p className="error">{interestError}</p>}
            <div className="marketing-interest-meta">
              Showing {filteredInterestEntries.length} of {interestEntries.length} entries.
            </div>
            <div className="marketing-interest-table-wrap">
              <table className="marketing-interest-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>From year</th>
                    <th>Source</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredInterestEntries.length === 0 ? (
                    <tr><td colSpan={6}>{interestLoading ? 'Loading…' : 'No entries found.'}</td></tr>
                  ) : filteredInterestEntries.map((row) => (
                    <tr key={row.id}>
                      <td>{editingInterestId === row.id ? <input type="text" value={editingInterestForm.name} onChange={(e) => setEditingInterestForm((p) => ({ ...p, name: e.target.value }))} /> : (row.name || '—')}</td>
                      <td>{editingInterestId === row.id ? <input type="email" value={editingInterestForm.email} onChange={(e) => setEditingInterestForm((p) => ({ ...p, email: e.target.value }))} /> : (row.email || '—')}</td>
                      <td>{row.fromEditionYear ?? '—'}</td>
                      <td>{editingInterestId === row.id ? <input type="text" value={editingInterestForm.source} onChange={(e) => setEditingInterestForm((p) => ({ ...p, source: e.target.value }))} /> : (row.source || '—')}</td>
                      <td>{formatCreatedAt(row.createdAt)}</td>
                      <td>
                        {editingInterestId === row.id ? (
                          <>
                            <button type="button" className="btn btn-outline" onClick={() => handleSaveEditInterest(row.id)}>Save</button>
                            <button type="button" className="btn btn-outline" onClick={handleCancelEditInterest}>Cancel</button>
                          </>
                        ) : (
                          <>
                            <button type="button" className="btn btn-outline" onClick={() => handleStartEditInterest(row)}>Edit</button>
                            <button type="button" className="btn btn-danger" onClick={() => handleDeleteInterest(row.id)}>Delete</button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      )}

      {activeTab === 'schedule' && (
        <section className="schedule-editor-section" aria-label="Schedule editor">
          <ScheduleEditorPanel
            events={events}
            jumpEventId={scheduleJumpEventId}
            onJumpConsumed={() => setScheduleJumpEventId(null)}
            onScheduleSaved={handleScheduleSaved}
            onError={(msg) => setError(msg)}
          />
        </section>
      )}

      {/* Modal for add/edit event */}
      {modalOpen && (
        <div
          className="fm-modal-overlay"
          role="presentation"
          onClick={() => {
            setModalOpen(false);
            setEditingEventId(null);
            setNewEvent(createEmptyBasicsForm());
          }}
        >
          <div
            className="fm-modal fm-modal--xl"
            role="dialog"
            aria-modal="true"
            aria-labelledby="event-mgmt-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="fm-modal-header">
              <h2 className="fm-modal-title" id="event-mgmt-modal-title">
                {editingEventId ? 'Edit event' : 'Add event'}
              </h2>
              <button
                type="button"
                className="fm-modal-close"
                aria-label="Close"
                onClick={() => {
                  setModalOpen(false);
                  setEditingEventId(null);
                  setNewEvent(createEmptyBasicsForm());
                }}
              >
                ✕
              </button>
            </div>
            <form noValidate onSubmit={handleSubmit} className="event-form">
              <div className="fm-modal-body">
              <p className="event-form-modal-hint">
                Use the event card for live mode, dashboard toggles, bonus scoring, and memory book. This form is for title, edition year, calendar dates, capacity, and Google Sheet links.
              </p>
              <fieldset className="event-form-section">
                <legend className="event-form-section-title">Event basics</legend>
                <div className="form-group">
                  <label>Title:</label>
                  <input
                    name="eventTitle"
                    type="text"
                    value={newEvent.title}
                    onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
                    required
                  />
                  <ModalFieldRequiredError invalidNames={eventModalInvalidNames} fieldName="eventTitle" />
                </div>
                <div className="form-group">
                  <label>Edition year (optional):</label>
                  <input
                    type="number"
                    min={2000}
                    max={9999}
                    placeholder="e.g. 2026"
                    value={
                      newEvent.editionYear === '' || newEvent.editionYear == null
                        ? ''
                        : String(newEvent.editionYear)
                    }
                    onChange={(e) => {
                      const v = (e.target.value || '').trim();
                      setNewEvent({ ...newEvent, editionYear: v === '' ? '' : v });
                    }}
                  />
                  <small>
                    Public URLs use /YEAR/…. Leave blank to derive the year from the Saturday date when possible (new events still get a year from the date or current year if missing).
                  </small>
                </div>
                <div className="form-group">
                  <label>Saturday (main competition):</label>
                  <input
                    type="date"
                    value={newEvent.date}
                    onChange={(e) => {
                      const value = (e.target.value || '').trim();
                      setNewEvent((prev) => ({
                        ...prev,
                        date: value,
                        scheduleDayDates: value
                          ? computeScheduleDayDatesFromSaturday(value)
                          : emptyScheduleDayDatesState(),
                      }));
                    }}
                  />
                  <small>
                    Primary competition date on the event record. Choosing a date fills Wednesday through Monday (Wednesday = Saturday − 3 … Monday after party = Saturday + 2). You can adjust individual days below.
                  </small>
                </div>
                <div className="event-form-subsection">
                  <h4 className="event-form-subsection-title">Rest of event week</h4>
                  <p className="schedule-quick-help event-form-week-hint">
                    Auto-filled from Saturday. Edit a day only if it differs (e.g. travel week). These values drive the public schedule, memory book range, mail-merge tokens, and when the admin dashboard treats the event as started.
                  </p>
                  <div className="event-calendar-grid">
                    {SCHEDULE_GRID_DAY_IDS.map((id) => (
                      <label key={id} className="event-calendar-field">
                        <span className="event-calendar-label">{SCHEDULE_DAY_LABELS[id] || id}</span>
                        <input
                          type="date"
                          value={newEvent.scheduleDayDates[id] || ''}
                          onChange={(e) =>
                            setNewEvent((prev) => ({
                              ...prev,
                              scheduleDayDates: { ...prev.scheduleDayDates, [id]: e.target.value },
                            }))
                          }
                        />
                      </label>
                    ))}
                  </div>
                  <h4 className="event-form-subsection-title">Activity-specific overrides (optional)</h4>
                  <p className="schedule-quick-help event-form-week-hint">
                    Only if an activity is <strong>not</strong> on its usual weekday. Leave blank to use the week row above.
                  </p>
                  <div className="event-calendar-grid event-calendar-grid--activities">
                    {ACTIVITY_OVERRIDE_FIELDS.map(({ key, label, hint }) => (
                      <label key={key} className="event-calendar-field">
                        <span className="event-calendar-label">{label}</span>
                        <input
                          type="date"
                          value={newEvent.activityDates[key] || ''}
                          onChange={(e) =>
                            setNewEvent((prev) => ({
                              ...prev,
                              activityDates: { ...prev.activityDates, [key]: e.target.value },
                            }))
                          }
                        />
                        <span className="event-calendar-hint">{hint}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div className="form-group">
                  <label>Max freshlings:</label>
                  <input
                    type="number"
                    min="0"
                    value={newEvent.maxFreshlings}
                    onChange={(e) =>
                      setNewEvent({
                        ...newEvent,
                        maxFreshlings: e.target.value === '' ? '' : Number(e.target.value),
                      })
                    }
                  />
                  <small>Capacity for this event (defaults to 112).</small>
                </div>
              </fieldset>

              <fieldset className="event-form-section">
                <legend className="event-form-section-title">Registration import</legend>
                <div className="form-group">
                  <span className="event-calendar-label" style={{ display: 'block', marginBottom: 8 }}>
                    Source
                  </span>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <input
                      type="radio"
                      name="registrationImportSource"
                      checked={newEvent.registrationImportSource !== 'fury_api'}
                      onChange={() =>
                        setNewEvent({ ...newEvent, registrationImportSource: 'sheet' })
                      }
                    />
                    Google Sheets (legacy)
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      type="radio"
                      name="registrationImportSource"
                      checked={newEvent.registrationImportSource === 'fury_api'}
                      onChange={() =>
                        setNewEvent({ ...newEvent, registrationImportSource: 'fury_api' })
                      }
                    />
                    Fury Registration API (forward-looking events)
                  </label>
                </div>
                {newEvent.registrationImportSource === 'fury_api' ? (
                  <div className="form-group">
                    <label>Fury Registration event ID:</label>
                    <input
                      type="text"
                      value={newEvent.furyRegistrationEventId}
                      onChange={(e) =>
                        setNewEvent({ ...newEvent, furyRegistrationEventId: e.target.value })
                      }
                      placeholder="Event id from Fury Registration (GET /events/:id/registrations)"
                    />
                    <small>
                      Admin Dashboard &quot;Import all&quot; calls the Fury API using your Firebase ID token. Set{' '}
                      <code>FURY_REGISTRATION_API_BASE</code> on Cloud Functions. See docs/fury-registration-field-map.md.
                    </small>
                  </div>
                ) : null}
                {newEvent.registrationImportSource === 'fury_api' ? (
                  <div className="form-group">
                    <label>Fury crew / mentor registration event ID (optional):</label>
                    <input
                      type="text"
                      value={newEvent.furyMentorsRegistrationEventId}
                      onChange={(e) =>
                        setNewEvent({ ...newEvent, furyMentorsRegistrationEventId: e.target.value })
                      }
                      placeholder="evt-fm-2027-mentors-and-volunteer-registration"
                    />
                    <small>
                      Separate Fury event for Mentor &amp; Volunteer registration. Only registrations with the{' '}
                      <strong>Mentor</strong> add-on are imported as mentor flyers; other crew roles are ignored.
                    </small>
                  </div>
                ) : null}
                {newEvent.registrationImportSource === 'fury_api' ? (
                  <div className="form-group">
                    <label>Jump completion Fury offering ID:</label>
                    <input
                      type="text"
                      value={newEvent.furyJumpCompletionEventOfferingId}
                      onChange={(e) =>
                        setNewEvent({ ...newEvent, furyJumpCompletionEventOfferingId: e.target.value })
                      }
                      placeholder="Fury eventOfferingId for the Saturday jump bundle (refund per_unit_count)"
                    />
                    <small>
                      Required for <strong>Declare jumping done</strong> — same catalog id as the bundle line in
                      Fury. Must use <code>refundCalculationMode: per_unit_count</code> and match the denominator
                      below.
                    </small>
                  </div>
                ) : null}
                {newEvent.registrationImportSource === 'fury_api' ? (
                  <div className="form-group">
                    <label>Jump bundle denominator (Fury includedCount):</label>
                    <input
                      type="number"
                      min={1}
                      max={99}
                      value={newEvent.furyJumpCompletionDenominator}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === '') {
                          setNewEvent({ ...newEvent, furyJumpCompletionDenominator: '' });
                          return;
                        }
                        const n = parseInt(v, 10);
                        if (!Number.isFinite(n)) return;
                        setNewEvent({
                          ...newEvent,
                          furyJumpCompletionDenominator: Math.min(99, Math.max(1, n)),
                        });
                      }}
                    />
                    <small>Sent as <code>denominator</code> on jump-completion POST; must match the offering bundle size (usually 4).</small>
                  </div>
                ) : null}
              </fieldset>

              <fieldset className="event-form-section">
                <legend className="event-form-section-title">Data sources (Google Sheets)</legend>
                <div className="form-group">
                  <label>Freshlings Google Sheet (URL or ID):</label>
                  <input
                    type="text"
                    value={newEvent.freshlingsSpreadsheetId}
                    onChange={(e) => setNewEvent({ ...newEvent, freshlingsSpreadsheetId: e.target.value })}
                    placeholder="https://docs.google.com/... or spreadsheet ID"
                    disabled={newEvent.registrationImportSource === 'fury_api'}
                  />
                  <small>
                    {newEvent.registrationImportSource === 'fury_api'
                      ? 'Optional for API-backed events; sheet import is not used when Import all runs.'
                      : 'Link this event to a Google Sheet for importing freshlings. Share the sheet with the Firebase service account if using Import from Sheet.'}
                  </small>
                </div>
                <div className="event-form-advanced-note">
                  Sheet names and manifest tab rarely change. Adjust them only if your workbook uses non-default tab names.
                </div>
                <div className="event-form-advanced-grid">
                  <div className="form-group">
                    <label>Freshlings sheet name:</label>
                    <input
                      type="text"
                      value={newEvent.freshlingsSheetName}
                      onChange={(e) => setNewEvent({ ...newEvent, freshlingsSheetName: e.target.value })}
                      placeholder="Freshlings"
                      disabled={newEvent.registrationImportSource === 'fury_api'}
                    />
                  </div>
                  <div className="form-group">
                    <label>Mentors sheet name:</label>
                    <input
                      type="text"
                      value={newEvent.mentorsSheetName}
                      onChange={(e) => setNewEvent({ ...newEvent, mentorsSheetName: e.target.value })}
                      placeholder="Mentors"
                      disabled={newEvent.registrationImportSource === 'fury_api'}
                    />
                    <small>Sheet in the same workbook for importing mentors.</small>
                  </div>
                  <div className="form-group">
                    <label>Event Manifest sheet name:</label>
                    <input
                      type="text"
                      value={newEvent.eventManifestSheetName}
                      onChange={(e) => setNewEvent({ ...newEvent, eventManifestSheetName: e.target.value })}
                      placeholder="EventManifest"
                      disabled={newEvent.registrationImportSource === 'fury_api'}
                    />
                    <small>Tab with ticket data (email, Event, Source, Date, Payment, Count) used for Event Summary.</small>
                  </div>
                </div>
              </fieldset>
              </div>
              <div className="fm-modal-footer">
                <button
                  type="button"
                  className="fm-modal-btn fm-modal-btn-cancel"
                  onClick={() => {
                    setModalOpen(false);
                    setEditingEventId(null);
                    setNewEvent(createEmptyBasicsForm());
                  }}
                >
                  Cancel
                </button>
                <button type="submit" className="fm-modal-btn fm-modal-btn-primary">
                  {editingEventId ? 'Update event' : 'Add event'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default EventManagement;
